import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { CalendarModule } from 'primeng/calendar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { IftaLabelModule } from 'primeng/iftalabel';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ContributionAdminService } from 'src/app/shared/services/contribution-admin.service';
import {
  Contribution,
  ContributionStatus,
  PaymentMethod,
  ContributionFilters,
  PaginationParams,
} from 'src/app/shared/models/contribution.models';

@Component({
  selector: 'app-list-contributions',
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    ToastModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
    CalendarModule,
    FormsModule,
    ConfirmDialogModule,
    IftaLabelModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './list-contributions.component.html',
})
export class ListContributionsComponent implements OnInit, OnDestroy {
  contributionService = inject(ContributionAdminService);
  router = inject(Router);
  notificationService = inject(NotificationService);
  confirmationService = inject(ConfirmationService);

  contributions: Contribution[] = [];
  loading = false;
  markingAsPaidId: string | null = null;

  // Expose enums to template
  ContributionStatus = ContributionStatus;
  PaymentMethod = PaymentMethod;

  // Filters
  searchText = '';
  statusFilter: ContributionStatus | null = null;
  paymentMethodFilter: PaymentMethod | null = null;
  startDate: Date | null = null;
  endDate: Date | null = null;

  // Search debouncing
  private searchSubject = new Subject<string>();

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalRecords = 0;
  totalPages = 0;

  // Dropdown options
  statusOptions = [
    { label: 'All', value: null },
    { label: 'Pending Payment', value: ContributionStatus.PENDING_PAYMENT },
    { label: 'Paid', value: ContributionStatus.PAID },
    { label: 'Failed', value: ContributionStatus.FAILED },
    { label: 'Canceled', value: ContributionStatus.CANCELED },
    { label: 'Refunded', value: ContributionStatus.REFUNDED },
  ];

  paymentMethodOptions = [
    { label: 'All', value: null },
    { label: 'Card', value: PaymentMethod.CARD },
    { label: 'Bank Transfer', value: PaymentMethod.BANK_TRANSFER },
  ];

  pageSizeOptions = [
    { label: '20', value: 20 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ];

  ngOnInit(): void {
    // Setup search debouncing
    this.searchSubject
      .pipe(
        debounceTime(300), // Wait 300ms after user stops typing
        distinctUntilChanged() // Only emit if value changed
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.loadContributions();
      });

    this.loadContributions();
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  loadContributions(): void {
    this.loading = true;

    const filters: ContributionFilters = {};
    if (this.searchText) filters.search = this.searchText;
    if (this.statusFilter) filters.status = this.statusFilter;
    if (this.paymentMethodFilter)
      filters.paymentMethod = this.paymentMethodFilter;
    if (this.startDate) filters.startDate = this.startDate;
    if (this.endDate) filters.endDate = this.endDate;

    const pagination: PaginationParams = {
      page: this.currentPage,
      limit: this.pageSize,
    };

    this.contributionService.listContributions(filters, pagination).subscribe({
      next: (response) => {
        this.contributions = response.data;
        this.totalRecords = response.pagination.total;
        this.totalPages = response.pagination.totalPages;
        this.loading = false;
      },
      error: () => {
        this.notificationService.error('Failed to load contributions');
        this.loading = false;
      },
    });
  }

  onSearchChange(): void {
    // Emit search value to debounced subject
    this.searchSubject.next(this.searchText);
  }

  onFilterChange(): void {
    // Reset to page 1 when filters change
    this.currentPage = 1;
    this.loadContributions();
  }

  resetFilters(): void {
    this.searchText = '';
    this.statusFilter = null;
    this.paymentMethodFilter = null;
    this.startDate = null;
    this.endDate = null;
    this.currentPage = 1;
    this.loadContributions();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadContributions();
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.loadContributions();
  }

  viewDetails(contributionId: string): void {
    this.router.navigate(['/admin/contributions', contributionId]);
  }

  getStatusLabel(status: ContributionStatus): string {
    const labels: Record<ContributionStatus, string> = {
      [ContributionStatus.PENDING_PAYMENT]: 'Pending Payment',
      [ContributionStatus.PAID]: 'Paid',
      [ContributionStatus.FAILED]: 'Failed',
      [ContributionStatus.CANCELED]: 'Canceled',
      [ContributionStatus.REFUNDED]: 'Refunded',
    };
    return labels[status] || status;
  }

  getStatusClasses(status: ContributionStatus): string {
    const classes: Record<ContributionStatus, string> = {
      [ContributionStatus.PENDING_PAYMENT]: 'bg-warning-100 text-warning-700',
      [ContributionStatus.PAID]: 'bg-validation-100 text-validation-700',
      [ContributionStatus.FAILED]: 'bg-error-100 text-error-700',
      [ContributionStatus.CANCELED]: 'bg-greyscale-100 text-greyscale-700',
      [ContributionStatus.REFUNDED]: 'bg-primary-100 text-primary-700',
    };
    return classes[status] || 'bg-greyscale-100 text-greyscale-700';
  }

  getTotalCO2(contribution: Contribution): number {
    return contribution.projectContributions.reduce(
      (sum, pc) => sum + Number(pc.co2Compensated),
      0
    );
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      timeZone: 'Europe/Brussels',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatAmount(amount: number | string): string {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `€${numAmount.toFixed(2)}`;
  }

  markAsPaid(contribution: Contribution): void {
    if (
      contribution.status !== ContributionStatus.PENDING_PAYMENT ||
      contribution.paymentMethod !== PaymentMethod.BANK_TRANSFER
    ) {
      return;
    }

    this.confirmationService.confirm({
      message:
        'Are you sure you want to mark this contribution as paid? This will generate a certificate and send a confirmation email to the user.',
      header: 'Confirm Payment Validation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, mark as paid',
      rejectLabel: 'Cancel',
      accept: () => this.executeMarkAsPaid(contribution),
    });
  }

  private executeMarkAsPaid(contribution: Contribution): void {
    this.markingAsPaidId = contribution.id;
    this.contributionService
      .markAsPaid(contribution.id, 'Marked as paid by admin via UI')
      .subscribe({
        next: () => {
          this.markingAsPaidId = null;
          this.notificationService.success('Contribution marked as paid');
          this.loadContributions();
        },
        error: (err) => {
          this.notificationService.error(
            err.error?.message || 'Failed to mark as paid'
          );
          this.markingAsPaidId = null;
        },
      });
  }
}
